/**
 * Les soldes d'ouverture saisis à la main, rubrique par rubrique : banque,
 * caisse, fournisseurs, dettes sociales et fiscales, subsides à recevoir,
 * immobilisations, fonds propres, autres actifs et passifs.
 *
 * ## En attendant les chiffres de la comptable
 *
 * Une ligne peut être créée **sans montant** : elle dit ce qui est attendu
 * (« solde ING au 31/12/2025, extrait à recevoir ») et bloque la validation
 * tant que le chiffre n'est pas là. Le travail avance sans rien inventer.
 *
 * ## Toujours justifiée
 *
 * Chaque saisie dit d'où vient son montant : l'extrait, le procès-verbal de
 * caisse, l'état du patrimoine, la facture. Une correction garde l'avant et
 * l'après dans l'historique ; un retrait exige un motif.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { messageErreur } from '../../../lib/payments/erreur';
import { lireLesLignes, retirerUneLigne, saisirUneLigne } from '../../../lib/compta/reprise/gateway.supabase';
import {
  comptesDeLaRubrique,
  controlerSaisie,
  ETATS_LIGNE,
  SENS,
  type LigneDeReprise,
  type Rubrique,
  type Saisie,
  type Sens,
} from '../../../lib/compta/reprise/types';
import SaisieMontant from '../components/SaisieMontant';
import { euros } from '../components/money';
import ChoixDuTiers from './ChoixDuTiers';
import TraitementCreance from './TraitementCreance';
import type { EtatDeLaReprise } from '../AdminRepriseOuverturePage';

const TYPE_DE_TIERS: Record<string, string> = {
  clients: 'ecole',
  avances_familles: 'ecole',
  fournisseurs: 'fournisseur',
  subsides_a_recevoir: 'organisme_subsidiant',
  dettes_sociales_fiscales: 'organisme_social',
};

export default function SaisiesOuverture({ etat, apresGeste, version }: {
  etat: EtatDeLaReprise; apresGeste: () => void; version: number;
}) {
  const { reprise, rubriques } = etat;
  const modifiable = reprise.statut === 'brouillon';
  const [lignes, setLignes] = useState<LigneDeReprise[]>([]);
  const [chargement, setChargement] = useState(true);
  /* La rubrique ou la ligne en cours d'edition : une seule a la fois. */
  const [edition, setEdition] = useState<{ rubrique: string; ligne: LigneDeReprise | null } | null>(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      /* Quelques dizaines de lignes au plus : une page large suffit, et le
         total dit si ce n'etait pas le cas. */
      const r = await lireLesLignes(reprise.id, { origine: 'manuel' }, 0, 500);
      setLignes(r.lignes);
      if (r.total > r.lignes.length) toast.error(`${r.total} saisies : seules les ${r.lignes.length} premières sont affichées`);
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setChargement(false);
    }
  }, [reprise.id]);

  useEffect(() => { void charger(); }, [charger, version]);

  /* Toutes les rubriques se saisissent : clients et acomptes pour les seuls
     tiers hors CDV (les familles viennent du calcul, onglet « Clients et
     familles »). */
  const manuelles = useMemo(() => [...rubriques].sort((a, b) => a.ordre - b.ordre), [rubriques]);

  if (chargement && lignes.length === 0) {
    return <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Lecture des saisies…</p>;
  }

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm text-gray-600">
        Les comptes proposés par rubrique sont une proposition technique d’après le plan validé. Un montant
        inconnu se laisse en attente : la ligne rappelle ce qui manque et bloque la validation, sans rien
        inventer.
      </p>
      {manuelles.map((r) => (
        <SectionRubrique key={r.code} rubrique={r} etat={etat} modifiable={modifiable}
          lignes={lignes.filter((l) => l.rubrique === r.code)}
          edition={edition?.rubrique === r.code ? edition : null}
          editer={(ligne) => setEdition({ rubrique: r.code, ligne })}
          fermer={() => setEdition(null)}
          apresGeste={() => { setEdition(null); apresGeste(); }} />
      ))}
    </div>
  );
}

function SectionRubrique({ rubrique, etat, modifiable, lignes, edition, editer, fermer, apresGeste }: {
  rubrique: Rubrique;
  etat: EtatDeLaReprise;
  modifiable: boolean;
  lignes: LigneDeReprise[];
  edition: { ligne: LigneDeReprise | null } | null;
  editer: (ligne: LigneDeReprise | null) => void;
  fermer: () => void;
  apresGeste: () => void;
}) {
  const [retrait, setRetrait] = useState<{ id: string; motif: string } | null>(null);
  const [traitement, setTraitement] = useState<string | null>(null);
  const debit = lignes.filter((l) => l.sens === 'debit').reduce((t, l) => t + (l.montantValideCents ?? 0), 0);
  const credit = lignes.filter((l) => l.sens === 'credit').reduce((t, l) => t + (l.montantValideCents ?? 0), 0);

  const retirer = async () => {
    if (!retrait) return;
    try {
      await retirerUneLigne(retrait.id, retrait.motif.trim());
      toast.success('Ligne retirée ; le motif reste à l’historique');
      setRetrait(null);
      apresGeste();
    } catch (e) {
      toast.error(messageErreur(e), { duration: 8000 });
    }
  };

  return (
    <section className="rounded-lg border border-gray-200 bg-white">
      <header className="flex flex-wrap items-start justify-between gap-2 border-b border-gray-100 px-4 py-3">
        <div>
          <h3 className="font-medium text-gray-900">{rubrique.libelle}</h3>
          {rubrique.aide && <p className="max-w-2xl text-xs text-gray-500">{rubrique.aide}</p>}
          {rubrique.saisie === 'hors_cdv' && (
            <p className="max-w-2xl text-xs text-indigo-700">Ici, seulement les clients hors CDV (école, commune…) ; les familles sont calculées.</p>
          )}
        </div>
        <div className="text-right text-sm tabular-nums text-gray-700">
          {debit > 0 && <div>Débit {euros(debit)}</div>}
          {credit > 0 && <div>Crédit {euros(credit)}</div>}
        </div>
      </header>

      {lignes.length > 0 && (
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <tbody className="divide-y divide-gray-100">
            {lignes.map((l) => (
              <tr key={l.id} className={clsx(edition?.ligne?.id === l.id && 'bg-indigo-50/40')}>
                <td className="px-4 py-2">
                  <div className="text-gray-900">{l.compteCode} {l.compteLibelle}{l.tiersNom && <> · {l.tiersNom}</>}</div>
                  {l.libelle && <div className="text-xs text-gray-600">{l.libelle}</div>}
                  <div className="text-xs text-gray-500">{l.justification}</div>
                  {(l.douteuxCents > 0 || l.eteintCents > 0) && (
                    <div className="text-xs text-amber-800">
                      {l.douteuxCents > 0 && <>douteuse {euros(l.douteuxCents)} (réduction {euros(l.reductionValeurCents)}) </>}
                      {l.eteintCents > 0 && <>éteinte {euros(l.eteintCents)}</>}
                    </div>
                  )}
                  {traitement === l.id && (
                    <div className="mt-2 rounded border border-indigo-200 bg-indigo-50/40 p-3">
                      <TraitementCreance ligne={l} montantModifiable={false}
                        apresGeste={() => { setTraitement(null); apresGeste(); }} />
                    </div>
                  )}
                </td>
                <td className="px-4 py-2 text-gray-600">{SENS[l.sens]}</td>
                <td className="px-4 py-2 text-right tabular-nums">{l.montantValideCents === null ? '—' : euros(l.montantValideCents)}</td>
                <td className="px-4 py-2">
                  <span className={clsx('rounded-full px-2 py-0.5 text-xs', ETATS_LIGNE[l.etat].classe)}>{ETATS_LIGNE[l.etat].label}</span>
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-right">
                  {modifiable && (
                    <>
                      {l.compteCode === '400000' && l.sens === 'debit' && l.montantValideCents !== null && (
                        <button type="button" onClick={() => setTraitement(traitement === l.id ? null : l.id)}
                          className="mr-1 text-xs text-indigo-700 hover:underline">
                          douteuse / éteinte
                        </button>
                      )}
                      <button type="button" aria-label="Corriger" onClick={() => editer(l)} className="p-1 text-gray-500 hover:text-indigo-700">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button type="button" aria-label="Retirer" onClick={() => setRetrait({ id: l.id, motif: '' })} className="p-1 text-gray-500 hover:text-red-700">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {retrait && (
        <div className="space-y-2 border-t border-gray-100 bg-red-50/40 px-4 py-3">
          <label className="block text-sm text-gray-700">
            Pourquoi retirer cette ligne ?
            <input value={retrait.motif} onChange={(e) => setRetrait({ ...retrait, motif: e.target.value })}
              className="mt-1 block w-full rounded border border-gray-300 px-2 py-1 text-sm" />
          </label>
          <div className="flex gap-2">
            <button type="button" disabled={retrait.motif.trim() === ''} onClick={retirer}
              className="rounded bg-red-600 px-3 py-1 text-sm text-white hover:bg-red-700 disabled:opacity-50">Retirer</button>
            <button type="button" onClick={() => setRetrait(null)} className="px-3 py-1 text-sm text-gray-600 hover:underline">Annuler</button>
          </div>
        </div>
      )}

      {edition ? (
        <FormulaireSaisie rubrique={rubrique} etat={etat} ligne={edition.ligne} fermer={fermer} apresGeste={apresGeste} />
      ) : modifiable && (
        <div className="px-4 py-2">
          <button type="button" onClick={() => editer(null)} className="inline-flex items-center gap-1 text-sm text-indigo-700 hover:underline">
            <Plus className="h-4 w-4" /> Ajouter une ligne
          </button>
        </div>
      )}
    </section>
  );
}

function FormulaireSaisie({ rubrique, etat, ligne, fermer, apresGeste }: {
  rubrique: Rubrique; etat: EtatDeLaReprise; ligne: LigneDeReprise | null; fermer: () => void; apresGeste: () => void;
}) {
  const comptes = useMemo(() => comptesDeLaRubrique(rubrique, etat.comptes, etat.rubriques), [rubrique, etat.comptes, etat.rubriques]);
  const [s, setS] = useState<Saisie>(() => ({
    rubrique: rubrique.code,
    compteCode: ligne?.compteCode ?? (comptes.length === 1 ? comptes[0].code : ''),
    sens: ligne?.sens ?? rubrique.sensHabituel,
    montantCents: ligne ? ligne.montantValideCents : 0,
    justification: ligne?.justification ?? '',
    tiersId: ligne?.tiersId ?? null,
    libelle: ligne?.libelle ?? '',
  }));
  const [envoi, setEnvoi] = useState(false);
  const enAttente = s.montantCents === null;
  const compte = etat.comptes.find((c) => c.code === s.compteCode);
  const refus = controlerSaisie(s, etat.rubriques, etat.comptes);

  const enregistrer = async () => {
    setEnvoi(true);
    try {
      await saisirUneLigne(etat.reprise.id, s, ligne?.id ?? null);
      toast.success(ligne ? 'Saisie corrigée' : enAttente ? 'Ligne créée, en attente du chiffre' : 'Saisie enregistrée');
      apresGeste();
    } catch (e) {
      toast.error(messageErreur(e), { duration: 8000 });
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="space-y-3 border-t border-gray-100 bg-indigo-50/30 px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2">Compte
          <select value={s.compteCode} onChange={(e) => setS({ ...s, compteCode: e.target.value, tiersId: null })}
            className="rounded border border-gray-300 px-2 py-1 text-sm">
            <option value="">—</option>
            {comptes.map((c) => <option key={c.code} value={c.code}>{c.code} {c.libelle}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2">Sens
          <select value={s.sens} onChange={(e) => setS({ ...s, sens: e.target.value as Sens })}
            className="rounded border border-gray-300 px-2 py-1 text-sm">
            <option value="debit">Débit</option>
            <option value="credit">Crédit</option>
          </select>
        </label>
        <label className="flex items-center gap-2">Montant
          <SaisieMontant valeurCents={s.montantCents ?? 0} disabled={enAttente} className="w-32"
            onChange={(c) => setS({ ...s, montantCents: c })} ariaLabel="Montant d’ouverture" />
        </label>
        <label className="flex items-center gap-2 text-gray-700">
          <input type="checkbox" checked={enAttente} onChange={(e) => setS({ ...s, montantCents: e.target.checked ? null : 0 })} />
          en attente du chiffre de la comptable
        </label>
      </div>
      {s.montantCents === 0 && (
        <p className="text-xs text-amber-800">
          Montant à zéro : la ligne dira que le solde est nul au 31/12/2025 (caisse vide, compte soldé). Si le
          chiffre n’est pas encore connu, cocher « en attente ».
        </p>
      )}
      {s.sens !== rubrique.sensHabituel && (
        <p className="text-xs text-amber-800">
          Sens inhabituel pour « {rubrique.libelle} » ({SENS[rubrique.sensHabituel].toLowerCase()} d’ordinaire) : un
          découvert bancaire ou un résultat négatif reporté, par exemple.
        </p>
      )}
      {compte?.tiersObligatoire && (
        <div className="space-y-1">
          <span className="text-gray-700">Tiers (obligatoire sur {compte.code})</span>
          <ChoixDuTiers tiersId={s.tiersId} onChange={(id, type) => setS({ ...s, tiersId: id, tiersType: type })}
            typeSuggere={TYPE_DE_TIERS[rubrique.code] ?? 'autre'} horsCdv={rubrique.saisie === 'hors_cdv'} />
        </div>
      )}
      <label className="block">Libellé (facultatif)
        <input value={s.libelle} onChange={(e) => setS({ ...s, libelle: e.target.value })} placeholder="ING compte principal, facture 2025-123…"
          className="mt-1 block w-full rounded border border-gray-300 px-2 py-1 text-sm" />
      </label>
      <label className="block">Justification <span className="text-red-600">(obligatoire)</span>
        <textarea value={s.justification} rows={2} onChange={(e) => setS({ ...s, justification: e.target.value })}
          placeholder={enAttente ? 'Ce qui est attendu : extrait ING au 31/12/2025, état du patrimoine…' : 'Extrait n°…, procès-verbal de caisse du 31/12/2025, état du patrimoine…'}
          className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm" />
      </label>
      {refus.length > 0 && <ul className="list-disc pl-5 text-xs text-amber-800">{refus.map((r) => <li key={r}>{r}</li>)}</ul>}
      <div className="flex gap-2">
        <button type="button" disabled={refus.length > 0 || envoi} onClick={enregistrer}
          className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-1.5 font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
          {envoi && <Loader2 className="h-4 w-4 animate-spin" />} {ligne ? 'Enregistrer la correction' : 'Enregistrer'}
        </button>
        <button type="button" onClick={fermer} className="px-3 py-1.5 text-gray-600 hover:underline">Annuler</button>
      </div>
    </div>
  );
}
